import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../../components/AdminPage';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, PagerButton, SavedNotice, useNow } from '../../components/feedback';
import { TextField } from '../../components/fields';
import { WriteDialog } from '../../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { NewTicketDialog } from './NewTicketDialog';
import { MAX_SAVED_VIEWS, readSavedViews, removeView, saveView, type SavedView } from './savedViews';
import {
  QUEUE_VIEWS,
  TicketFilters,
  queueQuery,
  queueSearch,
  readQueueState,
  type QueueState,
} from './TicketFilters';
import { TicketList, moveTicketFocus, type TicketDisplay } from './TicketList';
import { TicketSearch } from './TicketSearch';
import { useTicketKeys } from './useTicketKeys';

/** The notice a page leaves for the queue when it sends the person back here (a ticket handed back). */
export interface QueueNoticeState {
  notice: string;
}

function noticeOf(state: unknown): string | null {
  const notice = (state as Partial<QueueNoticeState> | null)?.notice;
  return typeof notice === 'string' ? notice : null;
}

/**
 * Trace's ticket queue (/tickets), close to Linear: the Inbox, My tickets and All views (with the caller's counts) and
 * the staff member's saved views; search (⌘K); filters by customer, age, status, priority, label, assignee, response
 * target and app; the tickets grouped by status as a list or a board. Everything lives in the address. Keyboard: C new
 * ticket (staff who work tickets), J and K move between tickets. Each ticket opens as its own page.
 */
export function TicketsPage() {
  const { api } = useAdmin();
  const { person } = useStaff();
  const abilities = useAbilities();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = readQueueState(params);
  const now = useNow();

  const key = abilities.readTickets
    ? `ticket-queue\u0000${queueSearch({ ...state, display: 'list' })}`
    : null;
  const read = useAdminRead(key, (signal) => api.listTicketQueue(queueQuery(state, person.userId), signal));
  const queue = read.dataKey === key && !read.failure ? read.data : null;
  // Counts stay from the last answer while a new view loads, so the view tabs do not flicker.
  const counts = read.data?.counts ?? null;
  const labelsRead = useAdminRead(abilities.readTickets ? 'ticket-labels' : null, (signal) =>
    api.listTicketLabels(signal),
  );
  const labels = labelsRead.data?.items ?? [];
  // The customer menu's organizations: read only once the menu is opened or the address names a customer, so the
  // queue alone never lists every organization (a listing is audited).
  const [customersWanted, setCustomersWanted] = useState(false);
  const customersRead = useAdminRead(
    abilities.readTickets && (customersWanted || state.org) ? 'ticket-customers' : null,
    (signal) => api.listOrganizations({ limit: 100 }, signal),
  );
  const customers = customersRead.data?.items ?? null;

  const [savedViews, setSavedViews] = useState<SavedView[]>(() => readSavedViews(person.userId));
  const savedSearch = queueSearch({ ...state, cursor: '' }, { withCursor: false });
  const activeSaved = useMemo(
    () => savedViews.find((v) => v.search === savedSearch) ?? null,
    [savedViews, savedSearch],
  );
  const [notice, setNotice] = useState<string | null>(() => noticeOf(location.state));
  const [saving, setSaving] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewNameError, setViewNameError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const go = (next: QueueState) => setParams(new URLSearchParams(queueSearch(next)));
  const setDisplay = (display: TicketDisplay) => go({ ...state, display });

  useTicketKeys(
    {
      ...(abilities.manageTickets ? { c: () => setCreating(true) } : {}),
      j: () => moveTicketFocus(1),
      k: () => moveTicketFocus(-1),
    },
    !saving && !creating,
  );

  let body;
  if (!abilities.readTickets) {
    body = <p className="admin-muted">Your staff role does not include tickets.</p>;
  } else if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the ticket queue" onRetry={read.retry} />;
  } else if (!queue) {
    body = <LoadingBlock label="Loading the tickets" />;
  } else if (queue.items.length === 0) {
    body = (
      <p className="admin-muted admin-ticket-empty">
        {state.view === 'inbox'
          ? 'Your inbox is empty. New escalations and replies land here.'
          : 'No ticket matches these filters.'}
      </p>
    );
  } else {
    body = (
      <div aria-busy={read.loading || undefined}>
        <TicketList items={queue.items} display={state.display} now={now} />
        {queue.next || state.cursor ? (
          <div className="admin-pager">
            <PagerButton onClick={() => go({ ...state, cursor: '' })} disabled={!state.cursor}>
              First page
            </PagerButton>
            <PagerButton onClick={() => go({ ...state, cursor: queue.next ?? '' })} disabled={!queue.next}>
              Next page
            </PagerButton>
          </div>
        ) : null}
      </div>
    );
  }

  const countOf = (view: string) =>
    view === 'inbox' ? counts?.inbox : view === 'mine' ? counts?.mine : undefined;

  return (
    <AdminPage title="Tickets">
      <PageHeading
        title="Tickets"
        description="Escalations from customers' help desks and tickets routed straight to Trace."
        actions={
          <div className="admin-heading-actions">
            <Link className="admin-action" to="/tickets/labels">
              Labels
            </Link>
            <Link className="admin-action" to="/tickets/settings">
              Response targets
            </Link>
            {abilities.manageTickets ? (
              <button
                type="button"
                className="admin-action admin-action--primary"
                onClick={() => setCreating(true)}
              >
                New ticket <kbd className="admin-kbd">C</kbd>
              </button>
            ) : null}
          </div>
        }
      />
      <SavedNotice message={notice} />
      {abilities.readTickets ? (
        <PageSection
          title="Queue"
          description="Grouped by status. J and K move between tickets; Enter opens one."
        >
          <div className="admin-ticket-toolbar">
            <nav className="admin-ticket-views" aria-label="Views">
              <ul>
                {QUEUE_VIEWS.map((view) => {
                  const count = countOf(view.value);
                  const current = !activeSaved && state.view === view.value;
                  return (
                    <li key={view.value}>
                      <Link
                        to={{
                          search: queueSearch({
                            ...readQueueState(new URLSearchParams()),
                            view: view.value,
                            display: state.display,
                          }),
                        }}
                        aria-current={current ? 'page' : undefined}
                      >
                        {view.label}
                        {count ? <span className="admin-ticket-views__count">{count}</span> : null}
                      </Link>
                    </li>
                  );
                })}
                {savedViews.map((view) => (
                  <li key={view.id}>
                    <Link
                      to={{ search: view.search }}
                      aria-current={activeSaved?.id === view.id ? 'page' : undefined}
                    >
                      {view.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="admin-segmented" role="group" aria-label="Display">
              <button
                type="button"
                aria-pressed={state.display === 'list'}
                onClick={() => setDisplay('list')}
              >
                List
              </button>
              <button
                type="button"
                aria-pressed={state.display === 'board'}
                onClick={() => setDisplay('board')}
              >
                Board
              </button>
            </div>
          </div>
          <div className="admin-ticket-toolbar">
            <TicketSearch value={state.q} onSearch={(q) => go({ ...state, q, cursor: '' })} />
            <TicketFilters
              state={state}
              labels={labels}
              customers={customers}
              onCustomersWanted={() => setCustomersWanted(true)}
              onChange={go}
            />
            {activeSaved ? (
              <button
                type="button"
                className="admin-action"
                onClick={() => {
                  const next = removeView(person.userId, activeSaved.id);
                  if (next) setSavedViews(next);
                  setNotice(
                    next
                      ? `The view ${activeSaved.name} is removed.`
                      : 'This browser could not change your saved views.',
                  );
                }}
              >
                Remove view
              </button>
            ) : (
              <button
                type="button"
                className="admin-action"
                aria-disabled={savedViews.length >= MAX_SAVED_VIEWS || undefined}
                onClick={() => {
                  if (savedViews.length >= MAX_SAVED_VIEWS) {
                    setNotice(`You have ${MAX_SAVED_VIEWS} saved views. Remove one to save another.`);
                    return;
                  }
                  setViewName('');
                  setViewNameError(null);
                  setSaving(true);
                }}
              >
                Save view
              </button>
            )}
          </div>
          {body}
        </PageSection>
      ) : (
        body
      )}
      <WriteDialog
        open={saving}
        onClose={() => setSaving(false)}
        title="Save view"
        description="Keeps this view and its filters in this browser, for you only."
        submitLabel="Save view"
        onSubmit={async () => {
          const name = viewName.trim();
          const error = !name ? 'Name the view.' : name.length > 40 ? 'Use at most 40 characters.' : null;
          setViewNameError(error);
          if (error) return 'invalid';
          const next = saveView(person.userId, name, savedSearch);
          if (!next) {
            setViewNameError('This browser could not store the view (its site data may be blocked).');
            return 'invalid';
          }
          setSavedViews(next);
          return undefined;
        }}
        onSaved={() => setNotice(`The view ${viewName.trim()} is saved.`)}
      >
        <TextField
          label="Name"
          value={viewName}
          onChange={setViewName}
          error={viewNameError}
          maxLength={40}
          required
        />
      </WriteDialog>
      {abilities.manageTickets ? (
        <NewTicketDialog
          key={creating ? 'open' : 'closed'}
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(ticket) => navigate(`/tickets/${ticket.number}`)}
        />
      ) : null}
    </AdminPage>
  );
}
